import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE = (import.meta as any).env.VITE_BASE_URL as string;
const DEMO_SCHEMA = 'univ';

function decodeToken(t: string): { e: string; p: string } | null {
  try {
    return JSON.parse(atob(decodeURIComponent(t)));
  } catch {
    return null;
  }
}

const OnboardingPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [creds, setCreds] = useState<{ e: string; p: string } | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'form' | 'logging-in' | 'done'>('form');

  // Read page/course from URL so the same onboard route works for both
  // casecrumbs.ai (page=case-crumbs) and sustainskills.ai (page=sustain-skills).
  const targetPage   = searchParams.get('page')        || 'case-crumbs';
  const targetCourse = searchParams.get('course_code') || 'SKYONE001';

  useEffect(() => {
    // Clear any existing session so the user cannot navigate to protected
    // routes without completing the form, even if they were previously logged in.
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('studentId');

    const t = searchParams.get('t');
    if (t) {
      const decoded = decodeToken(t);
      if (decoded) {
        setCreds(decoded);

        // Auto-fill + auto-submit when the website has already collected visitor info.
        const pName  = searchParams.get('name')  || '';
        const pEmail = searchParams.get('email') || '';
        const pPhone = searchParams.get('phone') || '';
        if (pName && pEmail && pPhone) {
          setName(pName);
          setEmail(pEmail);
          setPhone(pPhone);
          // Trigger submit after state has settled
          setStep('logging-in');
          setLoading(true);
          autoLogin(decoded, pName, pEmail, pPhone);
        }
      }
    }
  }, []);

  const autoLogin = async (
    credentials: { e: string; p: string },
    visitorName: string,
    visitorEmail: string,
    visitorPhone: string,
  ) => {
    try {
      const loginRes = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-schema': DEMO_SCHEMA },
        body: JSON.stringify({ email: credentials.e, password: credentials.p }),
      });
      const loginData = await loginRes.json();
      if (!loginData?.data?.token) {
        setStep('form');
        setLoading(false);
        return;
      }
      const token = loginData.data.token;
      const user  = loginData.data.user;

      let existingMetadata: any = {};
      try {
        const meRes = await fetch(`${API_BASE}/users/${user.id}`, {
          headers: { Authorization: `Bearer ${token}`, 'x-schema': DEMO_SCHEMA },
        });
        const meData = await meRes.json();
        existingMetadata = meData?.data?.metadata || meData?.metadata || {};
      } catch { /* ignore */ }

      const visitorLog: any[] = existingMetadata.visitor_log || [];
      visitorLog.push({ name: visitorName, email: visitorEmail, phone: visitorPhone, visited_at: new Date().toISOString() });

      await fetch(`${API_BASE}/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-schema': DEMO_SCHEMA },
        body: JSON.stringify({
          name: visitorName,
          phone: visitorPhone,
          alt_email: visitorEmail,
          metadata: { ...existingMetadata, visitor_log: visitorLog },
        }),
      });

      localStorage.setItem('studentId', user.id);
      setStep('done');
      setTimeout(() => {
        window.location.href = `/?sso_token=${encodeURIComponent(token)}&course_code=${encodeURIComponent(targetCourse)}&page=${encodeURIComponent(targetPage)}`;
      }, 600);
    } catch {
      setStep('form');
      setLoading(false);
    }
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!/^\+?[\d\s\-]{7,15}$/.test(phone.trim())) {
      setError('Please enter a valid phone number.');
      return;
    }

    setLoading(true);
    setError('');
    setStep('logging-in');

    try {
      const loginRes = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-schema': DEMO_SCHEMA },
        body: JSON.stringify({ email: creds!.e, password: creds!.p }),
      });
      const loginData = await loginRes.json();

      if (!loginData?.data?.token) {
        setError('Authentication failed. Please contact support.');
        setStep('form');
        setLoading(false);
        return;
      }

      const token = loginData.data.token;
      const user = loginData.data.user;

      // Fetch current metadata to append to visitor_log without overwriting other fields
      let existingMetadata: any = {};
      try {
        const meRes = await fetch(`${API_BASE}/users/${user.id}`, {
          headers: { Authorization: `Bearer ${token}`, 'x-schema': DEMO_SCHEMA },
        });
        const meData = await meRes.json();
        existingMetadata = meData?.data?.metadata || meData?.metadata || {};
      } catch { /* ignore */ }

      const visitorLog: any[] = existingMetadata.visitor_log || [];
      visitorLog.push({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        visited_at: new Date().toISOString(),
      });

      // Update profile: store visitor's real email in alt_email, keep system email intact
      await fetch(`${API_BASE}/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-schema': DEMO_SCHEMA,
        },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          alt_email: email.trim(),
          metadata: { ...existingMetadata, visitor_log: visitorLog },
        }),
      });

      // studentId is read by widget components to resolve default program filter
      localStorage.setItem('studentId', user.id);

      setStep('done');
      // Use sso_token query param so AuthProvider's existing SSO handler picks it
      // up on full reload — changing only the hash doesn't trigger a full reload,
      // so AuthProvider never re-runs and ProtectedRoute still sees unauthenticated.
      setTimeout(() => {
        window.location.href = `/?sso_token=${encodeURIComponent(token)}&course_code=${encodeURIComponent(targetCourse)}&page=${encodeURIComponent(targetPage)}`;
      }, 800);
    } catch {
      setError('Something went wrong. Please try again.');
      setStep('form');
      setLoading(false);
    }
  };

  const hasToken = Boolean(searchParams.get('t') && creds);

  if (!hasToken && !creds) {
    return (
      <div style={styles.errorWrap}>
        <div style={styles.errorCard}>
          <img src="https://falco-common.s3.ap-south-1.amazonaws.com/assets/Falco_Logo.svg" alt="Falco" style={styles.logo} />
          <h2 style={styles.errorTitle}>Invalid Link</h2>
          <p style={styles.errorText}>
            This onboarding link is invalid or has expired. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        {/* Brand header */}
        <div style={styles.header}>
          <img
            src="https://falco-common.s3.ap-south-1.amazonaws.com/assets/Falco_Logo.svg"
            alt="Falco"
            style={styles.logo}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
          <h1 style={styles.title}>Welcome</h1>
        </div>

        {step === 'done' ? (
          <div style={{ minHeight: 200 }} />
        ) : (
          <>
            <p style={styles.intro}>
              Please tell us a bit about yourself before you begin.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Full Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Priya Sharma"
                  style={styles.input}
                  required
                  disabled={loading}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="e.g. priya.sharma@company.com"
                  style={styles.input}
                  required
                  disabled={loading}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Phone Number *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="e.g. +91 98765 43210"
                  style={styles.input}
                  required
                  disabled={loading}
                />
              </div>

              {error && <p style={styles.errorMsg}>{error}</p>}

              <button type="submit" style={loading ? { ...styles.btn, opacity: 0.7 } : styles.btn} disabled={loading}>
                {loading ? 'Logging you in...' : 'Get Started →'}
              </button>
            </form>

            <p style={styles.note}>
              Your information is used solely for this session.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1464 0%, #211d70 40%, #2d2a8e 100%)',
    padding: '24px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    height: '52px',
    width: 'auto',
    maxWidth: '200px',
    objectFit: 'contain' as const,
    marginBottom: '16px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a1464',
    margin: '0 0 8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.4',
    margin: 0,
  },
  intro: {
    fontSize: '15px',
    color: '#444',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    letterSpacing: '0.3px',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1.5px solid #ddd',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
    color: '#222',
  },
  errorMsg: {
    color: '#d32f2f',
    fontSize: '13px',
    margin: 0,
    padding: '10px 14px',
    background: '#fdf0f0',
    borderRadius: '8px',
    border: '1px solid #f5c6cb',
  },
  btn: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #1a1464, #211d70)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    marginTop: '4px',
    transition: 'opacity 0.2s',
    fontFamily: 'inherit',
  },
  note: {
    fontSize: '12px',
    color: '#aaa',
    textAlign: 'center',
    marginTop: '20px',
    lineHeight: '1.4',
  },
  successWrap: {
    textAlign: 'center',
    padding: '32px 0',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#e8f5e9',
    color: '#2e7d32',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    fontWeight: 'bold',
  },
  successText: {
    color: '#444',
    fontSize: '16px',
  },
  errorWrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
    padding: '24px',
  },
  errorCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '400px',
    textAlign: 'center',
    boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
  },
  errorTitle: {
    color: '#333',
    fontSize: '22px',
    marginBottom: '12px',
  },
  errorText: {
    color: '#666',
    fontSize: '15px',
    lineHeight: '1.5',
  },
};

export default OnboardingPage;
